/**
 * External dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Tiptap from '@Components/TipTap';
import { UpdateVenuePayload } from '@Types/api';
import { Venue } from '@Types/venue';
import { useToastDiningOptions } from '../../hooks/useToastDiningOptions';
import { useUsers } from '@Hooks/useUsers';
import type { User } from '@Types/user';
import { useToastMenus } from '../../hooks/useToastMenus';
import { useToastRestaurants } from '../../hooks/useToastRestaurants';
import { useToastServiceCharges } from '../../hooks/useToastServiceCharges';
import service from '../../service';
import { Combobox } from '../Combobox';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

const VenueCategorySchema = z.enum([
  'HOTEL',
  'RESTAURANT',
  'BAR_LOUNGE',
  'NO_CATEGORY',
]);

const FormSchema = z.object({
  name: z.string().min(2, {
    message: 'A name is required.',
  }),
  // normalize description so empty strings become undefined
  description: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : undefined)),
  teaser: z.string().optional().nullable(),
  introText: z.string().optional().nullable(),
  enabled: z.boolean(),
  openTableBookingUrl: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : undefined))
    .pipe(z.httpUrl().optional()),
  openTableId: z.number().int().positive().nullable(),
  tripleSeatId: z.number().int().positive().nullable(),
  contactFishIds: z.string().default(''),
  toastRestaurantGuid: z.string().optional(),
  toastPrivateDiningMenuGuid: z.string().optional(),
  toastCorporateDiningMenuGuid: z.string().optional(),
  toastTicketedEventMenuGuid: z.string().optional(),
  toastTicketedEventServiceChargeGuid: z.string().optional(),
  toastTicketedEventDiningOptionGuid: z.string().optional(),
  category: VenueCategorySchema,
  membersOnly: z.boolean(),
  sendShiftReport: z.boolean(),
});

// Input type (before zod transforms/defaults) — used for the form's field values.
type FormInput = z.input<typeof FormSchema>;
// Output type (after transforms/defaults) — what handleSubmit receives.
type FormValues = z.infer<typeof FormSchema>;

export interface EditVenueSheetProps extends React.PropsWithChildren {
  venue: Venue;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

export default function EditVenueSheet({
  venue,
  isOpen,
  setIsOpen,
}: EditVenueSheetProps) {
  const queryClient = useQueryClient();

  // Event Manager assignment (managed outside the react-hook-form submit —
  // assignments have their own endpoints and apply immediately).
  const [selectedEventManagerId, setSelectedEventManagerId] = useState('');
  const [eventManagerBusy, setEventManagerBusy] = useState(false);

  const { data: usersResponse } = useUsers(
    { searchParams: { 'page[size]': 200 }, enabled: isOpen },
  );
  const users: User[] = usersResponse?.data ?? [];

  const eventManagerAssignment = (venue.userAssignments ?? []).find(
    (assignment) => assignment.role === 'EVENT_MANAGER',
  );

  const refreshVenue = async () => {
    await queryClient.invalidateQueries({ queryKey: ['venue', venue.id] });
    await queryClient.invalidateQueries({ queryKey: ['venues'] });
  };

  const handleAssignEventManager = async () => {
    if (!selectedEventManagerId || eventManagerBusy) return;
    setEventManagerBusy(true);
    try {
      await service.assignVenueUser(venue.id, selectedEventManagerId);
      await refreshVenue();
      setSelectedEventManagerId('');
      toastSuccess('Event Manager assigned');
    } catch (error) {
      console.error('Error assigning event manager:', error);
      toastError('We were unable to assign this Event Manager.');
    } finally {
      setEventManagerBusy(false);
    }
  };

  const handleRemoveEventManager = async (assignmentId: string) => {
    if (eventManagerBusy) return;
    setEventManagerBusy(true);
    try {
      await service.removeVenueAssignment(venue.id, assignmentId);
      await refreshVenue();
      toastSuccess('Event Manager removed');
    } catch (error) {
      console.error('Error removing event manager:', error);
      toastError('We were unable to remove this Event Manager.');
    } finally {
      setEventManagerBusy(false);
    }
  };

  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: venue.name,
      description: venue.description ?? '',
      teaser: venue.teaser ?? '',
      introText: venue.introText ?? '',
      enabled: venue.disabledAt ? false : true,
      openTableBookingUrl: venue.openTableBookingUrl ?? undefined,
      openTableId: venue.openTableId ?? null,
      tripleSeatId: venue.tripleSeatId ?? null,
      contactFishIds: (venue.contactFishIds ?? []).join('\n'),
      toastRestaurantGuid: venue.toastRestaurantGuid ?? undefined,
      toastPrivateDiningMenuGuid: venue.toastPrivateDiningMenuGuid ?? undefined,
      toastCorporateDiningMenuGuid:
        venue.toastCorporateDiningMenuGuid ?? undefined,
      toastTicketedEventMenuGuid: venue.toastTicketedEventMenuGuid ?? undefined,
      toastTicketedEventServiceChargeGuid:
        venue.toastTicketedEventServiceChargeGuid ?? undefined,
      toastTicketedEventDiningOptionGuid:
        venue.toastTicketedEventDiningOptionGuid ?? undefined,
      category: venue.category?.value as
        | 'HOTEL'
        | 'RESTAURANT'
        | 'BAR_LOUNGE'
        | 'NO_CATEGORY',
      membersOnly: venue.membersOnly || false,
      sendShiftReport: venue.sendShiftReport || false,
    },
  });

  // Watch the currently selected Toast restaurant in the form
  const selectedRestaurantGuid = form.watch('toastRestaurantGuid');
  const effectiveRestaurantGuid =
    selectedRestaurantGuid || venue.toastRestaurantGuid || '';

  // Toast restaurants (for the restaurant combobox)
  const toastRestaurants = useToastRestaurants({
    enabled: isOpen,
  });

  const toastRestaurantItems = (toastRestaurants.data ?? [])
    .map((restaurant) => ({
      value: restaurant.guid,
      label:
        restaurant.general?.name ??
        restaurant.general?.locationName ??
        restaurant.guid,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  // Toast menus for the *currently selected* restaurant
  const toastMenus = useToastMenus({
    restaurantGuid: effectiveRestaurantGuid,
    enabled: !!effectiveRestaurantGuid && isOpen,
  });

  // Toast service charges for the *currently selected* restaurant
  const toastServiceCharges = useToastServiceCharges({
    restaurantGuid: effectiveRestaurantGuid,
    enabled: !!effectiveRestaurantGuid && isOpen,
  });

  // Toast dining options for the *currently selected* restaurant
  const toastDiningOptions = useToastDiningOptions({
    restaurantGuid: effectiveRestaurantGuid,
    enabled: !!effectiveRestaurantGuid && isOpen,
  });

  const toastMenuItems = (toastMenus.data ?? [])
    .map((menu) => ({
      value: menu.guid,
      label: menu.name ?? menu.guid,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const toastServiceChargeItems = (toastServiceCharges.data ?? [])
    .map((serviceCharge) => ({
      value: serviceCharge.guid,
      label: serviceCharge.name ?? serviceCharge.guid,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const toastDiningOptionItems = (toastDiningOptions.data ?? [])
    .map((diningOption) => ({
      value: diningOption.guid,
      label: diningOption.name ?? diningOption.guid,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  // Keep form in sync if venue changes while sheet is open
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: venue.name,
        description: venue.description ?? '',
        teaser: venue.teaser ?? '',
        introText: venue.introText ?? '',
        enabled: venue.disabledAt ? false : true,
        openTableBookingUrl: venue.openTableBookingUrl ?? undefined,
        openTableId: venue.openTableId ?? null,
        tripleSeatId: venue.tripleSeatId ?? null,
        contactFishIds: (venue.contactFishIds ?? []).join('\n'),
        toastRestaurantGuid: venue.toastRestaurantGuid ?? undefined,
        toastPrivateDiningMenuGuid:
          venue.toastPrivateDiningMenuGuid ?? undefined,
        toastCorporateDiningMenuGuid:
          venue.toastCorporateDiningMenuGuid ?? undefined,
        toastTicketedEventMenuGuid:
          venue.toastTicketedEventMenuGuid ?? undefined,
        toastTicketedEventServiceChargeGuid:
          venue.toastTicketedEventServiceChargeGuid ?? undefined,
        toastTicketedEventDiningOptionGuid:
          venue.toastTicketedEventDiningOptionGuid ?? undefined,
        category: venue.category?.value as
          | 'HOTEL'
          | 'RESTAURANT'
          | 'BAR_LOUNGE'
          | 'NO_CATEGORY',
        membersOnly: venue.membersOnly ?? false,
        sendShiftReport: venue.sendShiftReport || false,
      });
    }
  }, [venue, isOpen, form]);

  // If the user selects a different Toast restaurant than the venue's current one,
  // clear the private dining menu selection so they must pick a menu for that restaurant.
  useEffect(() => {
    if (
      selectedRestaurantGuid &&
      selectedRestaurantGuid !== venue.toastRestaurantGuid
    ) {
      form.setValue('toastPrivateDiningMenuGuid', undefined);
      form.setValue('toastCorporateDiningMenuGuid', undefined);
    }
  }, [selectedRestaurantGuid, venue.toastRestaurantGuid, form]);

  const onSubmit = async (values: FormValues) => {
    const {
      name,
      description,
      teaser,
      introText,
      enabled,
      toastRestaurantGuid,
      toastPrivateDiningMenuGuid,
      toastCorporateDiningMenuGuid,
      toastTicketedEventMenuGuid,
      toastTicketedEventServiceChargeGuid,
      toastTicketedEventDiningOptionGuid,
      openTableBookingUrl,
      openTableId,
      tripleSeatId,
      contactFishIds,
      category,
      membersOnly,
      sendShiftReport,
    } = values;

    const body: UpdateVenuePayload = {
      name,
      ...(typeof description !== 'undefined' ? { description } : {}),
      ...(typeof teaser !== 'undefined' && teaser !== null
        ? { teaser: teaser.trim() }
        : {}),
      ...(typeof introText !== 'undefined' && introText !== null
        ? { introText: introText.trim() }
        : {}),
      disabledAt: enabled ? null : new Date().toISOString(),
      // Persist toast restaurant + menu selection
      ...(typeof toastRestaurantGuid !== 'undefined'
        ? { toastRestaurantGuid }
        : {}),
      ...(typeof toastPrivateDiningMenuGuid !== 'undefined'
        ? { toastPrivateDiningMenuGuid }
        : {}),
      ...(typeof toastCorporateDiningMenuGuid !== 'undefined'
        ? { toastCorporateDiningMenuGuid }
        : {}),
      ...(typeof toastTicketedEventMenuGuid !== 'undefined'
        ? { toastTicketedEventMenuGuid }
        : {}),
      ...(typeof toastTicketedEventServiceChargeGuid !== 'undefined'
        ? { toastTicketedEventServiceChargeGuid }
        : {}),
      ...(typeof toastTicketedEventDiningOptionGuid !== 'undefined'
        ? { toastTicketedEventDiningOptionGuid }
        : {}),
      openTableBookingUrl: openTableBookingUrl ?? null,
      openTableId: openTableId ?? null,
      tripleSeatId: tripleSeatId ?? null,
      contactFishIds: contactFishIds
        ? contactFishIds
            .split('\n')
            .map((email) => email.trim())
            .filter(Boolean)
        : [],
      category,
      membersOnly,
      sendShiftReport,
    };

    form.clearErrors('root');

    try {
      await service.updateVenue(venue.id, body);

      await queryClient.invalidateQueries({
        queryKey: ['venue-spaces', venue.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['venue', venue.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['venues'],
      });

      setIsOpen(false);
      toastSuccess('Venue updated successfully');
    } catch (error) {
      console.error('Error updating venue:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to update this venue. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      // reset back to the current venue values when closing
      form.reset({
        name: venue.name,
        description: venue.description ?? '',
        teaser: venue.teaser ?? '',
        introText: venue.introText ?? '',
        enabled: venue.disabledAt ? false : true,
        toastRestaurantGuid: venue.toastRestaurantGuid ?? undefined,
        toastPrivateDiningMenuGuid:
          venue.toastPrivateDiningMenuGuid ?? undefined,
        toastCorporateDiningMenuGuid:
          venue.toastCorporateDiningMenuGuid ?? undefined,
        toastTicketedEventMenuGuid:
          venue.toastTicketedEventMenuGuid ?? undefined,
        openTableBookingUrl: venue.openTableBookingUrl ?? undefined,
        openTableId: venue.openTableId ?? null,
        tripleSeatId: venue.tripleSeatId ?? null,
        contactFishIds: (venue.contactFishIds ?? []).join('\n'),
        category: venue.category?.value as
          | 'HOTEL'
          | 'RESTAURANT'
          | 'BAR_LOUNGE'
          | 'NO_CATEGORY',
        membersOnly: venue.membersOnly ?? false,
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>{`Edit ${venue.name}'s details`}</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {/* Top-level error banner */}
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  {/* Name */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Venue name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. The Steakhouse" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Description */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Tiptap
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Describe this venue..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Teaser */}
                  <FormField
                    control={form.control}
                    name="teaser"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teaser</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Short summary or tagline for this venue."
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Intro text */}
                  <FormField
                    control={form.control}
                    name="introText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Intro text</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Intro message sent from the stylist when a wedding is booked at this venue."
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* OpenTable Booking URL */}
                  <FormField
                    control={form.control}
                    name="openTableBookingUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>OpenTable Booking URL</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* OpenTable Restaurant ID */}
                  <FormField
                    control={form.control}
                    name="openTableId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>OpenTable Restaurant ID</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Numeric OpenTable restaurant ID used for reservations.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Contact Fish */}
                  <FormField
                    control={form.control}
                    name="contactFishIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Shift Report Contacts</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Enter contact emails, one per line..."
                            className="min-h-[100px]"
                          />
                        </FormControl>
                        <FormDescription>
                          Emails of contacts who will receive shift reports (one
                          per line).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-4 rounded-md bg-zinc-100 p-4">
                    {/* Toast section header */}
                    <div className="flex items-center gap-1">
                      <img
                        src="/toast-favicon.png"
                        alt="Toast Logo"
                        className="h-4 w-4"
                      />
                      <div className="text-sm font-bold text-zinc-500">
                        Toast Settings
                      </div>
                    </div>

                    {/* Toast restaurant selection */}
                    <FormField
                      control={form.control}
                      name="toastRestaurantGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Restaurant</FormLabel>
                          <Combobox
                            inForm
                            id="toastRestaurantGuid"
                            items={toastRestaurantItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select a Toast restaurant"
                            disabled={
                              toastRestaurants.isLoading ||
                              !toastRestaurants.data?.length
                            }
                            loading={toastRestaurants.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Choose which Toast location is linked to this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Private dining Toast menu */}
                    <FormField
                      control={form.control}
                      name="toastPrivateDiningMenuGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Private Dining Menu</FormLabel>
                          <Combobox
                            inForm
                            id="toastPrivateDiningMenuGuid"
                            items={toastMenuItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              effectiveRestaurantGuid
                                ? 'Select a Toast menu'
                                : 'Select a Toast restaurant first'
                            }
                            disabled={
                              !effectiveRestaurantGuid ||
                              toastMenus.isLoading ||
                              !toastMenus.data?.length
                            }
                            loading={toastMenus.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Select which Toast menu will be used for private
                            dining at this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Corporate dining Toast menu */}
                    <FormField
                      control={form.control}
                      name="toastCorporateDiningMenuGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Corporate Dining Menu</FormLabel>
                          <Combobox
                            inForm
                            id="toastCorporateDiningMenuGuid"
                            items={toastMenuItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              effectiveRestaurantGuid
                                ? 'Select a Toast menu'
                                : 'Select a Toast restaurant first'
                            }
                            disabled={
                              !effectiveRestaurantGuid ||
                              toastMenus.isLoading ||
                              !toastMenus.data?.length
                            }
                            loading={toastMenus.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Select which Toast menu will be used for corporate
                            dining at this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Ticketed Events Toast menu */}
                    <FormField
                      control={form.control}
                      name="toastTicketedEventMenuGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ticketed Events Menu</FormLabel>
                          <Combobox
                            inForm
                            id="toastTicketedEventMenuGuid"
                            items={toastMenuItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              effectiveRestaurantGuid
                                ? 'Select a Toast menu'
                                : 'Select a Toast restaurant first'
                            }
                            disabled={
                              !effectiveRestaurantGuid ||
                              toastMenus.isLoading ||
                              !toastMenus.data?.length
                            }
                            loading={toastMenus.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Select which Toast menu will be used for ticketed
                            events at this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Ticketed Events Service Charge */}
                    <FormField
                      control={form.control}
                      name="toastTicketedEventServiceChargeGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ticketed Events Service Charge</FormLabel>
                          <Combobox
                            inForm
                            id="toastTicketedEventServiceChargeGuid"
                            items={toastServiceChargeItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              effectiveRestaurantGuid
                                ? 'Select a service charge'
                                : 'Select a Toast restaurant first'
                            }
                            disabled={
                              !effectiveRestaurantGuid ||
                              toastServiceCharges.isLoading ||
                              !toastServiceCharges.data?.length
                            }
                            loading={toastServiceCharges.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Select which Toast service charge will be applied to
                            ticketed events at this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Ticketed Events Dining Option */}
                    <FormField
                      control={form.control}
                      name="toastTicketedEventDiningOptionGuid"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ticketed Events Dining Option</FormLabel>
                          <Combobox
                            inForm
                            id="toastTicketedEventDiningOptionGuid"
                            items={toastDiningOptionItems}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={
                              effectiveRestaurantGuid
                                ? 'Select a dining option'
                                : 'Select a Toast restaurant first'
                            }
                            disabled={
                              !effectiveRestaurantGuid ||
                              toastDiningOptions.isLoading ||
                              !toastDiningOptions.data?.length
                            }
                            loading={toastDiningOptions.isLoading}
                            inModal
                          />
                          <FormDescription>
                            Select which Toast dining option will be used for
                            ticketed events at this venue.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4 rounded-md bg-zinc-100 p-4">
                    {/* Tripleseat section header */}
                    <div className="flex items-center gap-1">
                      <img
                        src="/triple-seat-favicon-400x400.png"
                        alt="Tripleseat Logo"
                        className="h-4 w-4"
                      />
                      <div className="text-sm font-bold text-zinc-500">
                        Tripleseat Settings
                      </div>
                    </div>

                    {/* Tripleseat Location ID */}
                    <FormField
                      control={form.control}
                      name="tripleSeatId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tripleseat Location ID</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === ''
                                    ? null
                                    : Number(e.target.value),
                                )
                              }
                            />
                          </FormControl>
                          <FormDescription>
                            Numeric Location ID from Tripleseat used for event
                            bookings.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator
                    className="my-8 h-[1px] bg-zinc-200"
                    orientation="horizontal"
                  />

                  {/* Category */}
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger className="w-70">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="HOTEL">Hotel</SelectItem>
                            <SelectItem value="RESTAURANT">
                              Restaurant
                            </SelectItem>
                            <SelectItem value="BAR_LOUNGE">
                              Bar / Lounge
                            </SelectItem>
                            <SelectItem value="NO_CATEGORY">
                              No Category
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* MembersOnly */}
                  <FormField
                    control={form.control}
                    name="membersOnly"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                            />
                          </FormControl>
                          <FormLabel>Members Only</FormLabel>
                        </div>
                        <FormDescription>
                          Enable for Venues exclusive to Club Members
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* SendShiftReport */}
                  <FormField
                    control={form.control}
                    name="sendShiftReport"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                            />
                          </FormControl>
                          <FormLabel>Send Shift Report</FormLabel>
                        </div>
                        <FormDescription>
                          Enable to generate shift summaries for this venue
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Enabled */}
                  <FormField
                    control={form.control}
                    name="enabled"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                            />
                          </FormControl>
                          <FormLabel>Enabled</FormLabel>
                        </div>
                        <FormDescription>
                          Disabling a venue hides it from users.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator
                    className="my-8 h-[1px] bg-zinc-200"
                    orientation="horizontal"
                  />

                  {/* Event Manager (assigned immediately, outside the form submit) */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Event Manager</p>
                      <p className="text-muted-foreground text-sm">
                        {
                          "Fallback sender when a booking has no stylist — uses this staff member's dedicated SMS inbox."
                        }
                      </p>
                    </div>

                    {eventManagerAssignment ? (
                      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {eventManagerAssignment.user.name ||
                              eventManagerAssignment.user.email ||
                              eventManagerAssignment.user.id}
                          </p>
                          {eventManagerAssignment.user.email && (
                            <p className="text-muted-foreground truncate text-sm">
                              {eventManagerAssignment.user.email}
                            </p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={eventManagerBusy}
                          onClick={() =>
                            handleRemoveEventManager(eventManagerAssignment.id)
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Select
                          value={selectedEventManagerId}
                          onValueChange={setSelectedEventManagerId}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a staff member" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name || u.email || u.id}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="primary"
                          disabled={!selectedEventManagerId || eventManagerBusy}
                          onClick={handleAssignEventManager}
                        >
                          Assign
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={form.formState.isSubmitting}
                >
                  Update
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
